/**
 * SummaryGenerator — Résumé intelligent (Carbon Design System)
 */

import { useState } from 'react';
import { Document, ArrowLeft } from '@carbon/icons-react';
import { Button } from '@carbon/react';
import {
  FileUpload,
  LanguageSelector,
  AIProcessingCard,
  SummaryDisplay,
  AiGeneratorPageLayout,
  AiGeneratorFormCard,
  AiFormStep,
  AiGenerateButton,
} from '@sphere/learning/ui';
import {
  useGenerateSummary,
  useFileValidation,
} from '@sphere/learning/data-access';
import type { SupportedLanguage, SummarySchema } from '@sphere/ai/domain';
import { handleAIError } from '@sphere/ai/data-access';
import { downloadBlob } from '@sphere/shared/util';
import tw from './SummaryGenerator.tailwind.module.scss';

export default function SummaryGenerator() {
  const [language, setLanguage] = useState<SupportedLanguage>(
    'fr' as SupportedLanguage
  );
  const [generatedSummary, setGeneratedSummary] =
    useState<SummarySchema | null>(null);

  const { file, validation, setFile } = useFileValidation();
  const generatedSummaryMutation = useGenerateSummary();

  const handleGenerate = async () => {
    if (!file || !validation.isValid) return;
    try {
      const summary = await generatedSummaryMutation.mutateAsync({
        file,
        lang: language,
      });
      setGeneratedSummary(summary);
    } catch (error) {
      console.error('Summary generation failed:', error);
    }
  };

  const handleNewSummary = () => {
    setGeneratedSummary(null);
    setFile(null);
  };

  const handleDownloadSummary = () => {
    if (!generatedSummary) return;
    const content =
      `${generatedSummary.title || 'AI Summary'}\n\n` +
      `SUMMARY:\n${generatedSummary.summary}\n\n` +
      `KEY POINTS:\n${generatedSummary.key_points.map((p) => `• ${p}`).join('\n')}\n\n` +
      `STATS:\n` +
      `- Original words: ${generatedSummary.word_count.original.toLocaleString()}\n` +
      `- Summary words: ${generatedSummary.word_count.summary.toLocaleString()}\n` +
      `- Reduction: ${generatedSummary.word_count.reduction_percentage}%\n`;
    const blob = new Blob([content], { type: 'text/plain' });
    downloadBlob(blob, `summary-${Date.now()}.txt`);
  };

  if (generatedSummary) {
    return (
      <div className={tw.u1}>
        <div style={{ maxWidth: '90rem', margin: '0 auto' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={ArrowLeft}
              onClick={handleNewSummary}
            >
              Retour
            </Button>
          </div>
          <SummaryDisplay
            summary={generatedSummary}
            onDownload={handleDownloadSummary}
            onNewSummary={handleNewSummary}
          />
        </div>
      </div>
    );
  }

  return (
    <AiGeneratorPageLayout
      icon={Document}
      title="Générateur de Résumé IA"
      description="Transformez instantanément vos longs documents en résumés clairs et concis. Une solution professionnelle pour l'analyse, la synthèse et l'efficacité."
    >
      <AiGeneratorFormCard title="Créer un Résumé" icon={Document}>
        <AiFormStep step={1} label="Sélectionnez votre document">
          <FileUpload
            file={file}
            onFileChange={setFile}
            disabled={generatedSummaryMutation.isPending}
          />
          {validation.error && (
            <p className={`cds--form-requirement ${tw.u2}`}>{validation.error}</p>
          )}
        </AiFormStep>
        <AiFormStep step={2} label="Choisissez la langue">
          <LanguageSelector
            value={language}
            onChange={setLanguage}
            disabled={generatedSummaryMutation.isPending}
          />
        </AiFormStep>
        <AiFormStep step={3} label="Générez le résumé">
          <AiGenerateButton
            onClick={handleGenerate}
            disabled={
              !file || !validation.isValid || generatedSummaryMutation.isPending
            }
            isLoading={generatedSummaryMutation.isPending}
            label="Générer le Résumé"
            loadingLabel="Génération..."
            icon={Document}
          />
        </AiFormStep>
      </AiGeneratorFormCard>

      {generatedSummaryMutation.isPending && (
        <div className={tw.u3}>
          <AIProcessingCard
            isLoading
            error={null}
            title="Génération du résumé..."
            description="L'IA analyse votre texte et extrait les informations essentielles..."
          />
        </div>
      )}

      {generatedSummaryMutation.error && (
        <div className={tw.u3}>
          <AIProcessingCard
            isLoading={false}
            error={handleAIError(generatedSummaryMutation.error)}
            title="Erreur de génération"
            onRetry={handleGenerate}
          />
        </div>
      )}
    </AiGeneratorPageLayout>
  );
}
